import { useState } from 'react';

import type { InvestmentHoldingDto, InvestmentThesisDto } from '../../lib/api/endpoints';
import { Button } from '../ui/Button';

interface InvestmentIdentityConfirmationProps {
  holding: InvestmentHoldingDto;
  thesis: InvestmentThesisDto;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

export function InvestmentIdentityConfirmation({
  holding,
  thesis,
  isPending,
  onCancel,
  onConfirm,
}: InvestmentIdentityConfirmationProps) {
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState('');
  const meaningfulReason = reason.trim().length >= 10;

  return (
    <section
      aria-label="Confirm investment identity"
      className="mt-4 space-y-4 border-t border-border pt-4"
    >
      <div className="grid gap-3 text-sm sm:grid-cols-2">
        <div className="border-l-2 border-warn pl-3">
          <div className="font-semibold">Recorded thesis identity</div>
          <div className="mt-1 text-text-muted">HL code: {thesis.identity.value}</div>
          <div className="mt-1 break-words text-text-muted">
            Security fingerprint: {thesis.identity.fingerprint ?? 'Not recorded'}
          </div>
        </div>
        <div className="border-l-2 border-primary pl-3">
          <div className="font-semibold">Latest holding identity</div>
          <div className="mt-1 text-text-muted">HL code: {holding.code}</div>
          <div className="mt-1 break-words text-text-muted">Security name: {holding.stock}</div>
        </div>
      </div>

      <label className="flex items-start gap-2 text-sm">
        <input
          className="mt-1"
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
        />
        <span>I confirm that the recorded thesis and latest holding are the same investment.</span>
      </label>

      <label className="block text-sm text-text-muted">
        Reason for confirming this identity
        <textarea
          className="mt-1 min-h-20 w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/40"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
      {!meaningfulReason && reason.length > 0 ? (
        <p className="text-sm text-warn">Enter at least 10 characters for the audit reason.</p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={!confirmed || !meaningfulReason || isPending}
          onClick={() => onConfirm(reason.trim())}
        >
          {isPending ? 'Confirming...' : 'Confirm same investment'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  );
}
